import { useMemo } from "react";
import { Alert, Button, Loading, Modal, Typography } from "@/components/atoms";
import { GetSubscriptionPlansResponse, SubscriptionPlan } from "@/redux/apis/subscription/types";
import { extractPlanFeatures } from "../helpers";

interface ConfirmDowngradeModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm?: () => void;
  targetPlan?: SubscriptionPlan;
  subscriptionData?: GetSubscriptionPlansResponse;
  isLoading?: boolean;
}

export const ConfirmDowngradeModal = ({
  open,
  onClose,
  onConfirm,
  targetPlan,
  subscriptionData,
  isLoading = false,
}: ConfirmDowngradeModalProps) => {
  const planName = targetPlan?.subscriptionName || "New Plan";
  const iconSvg = targetPlan?.iconSvg;

  // Extract features from subscription data, or fall back to featuredFeatures
  const features = useMemo(() => {
    if (subscriptionData && targetPlan?.subscriptionId) {
      const extractedFeatures = extractPlanFeatures(subscriptionData, targetPlan.subscriptionId);
      if (extractedFeatures.length > 0) {
        return extractedFeatures;
      }
    }
    return targetPlan?.featuredFeatures || [];
  }, [subscriptionData, targetPlan]);

  const handleCancel = () => {
    if (!isLoading) {
      onClose();
    }
  };

  const handleConfirm = () => {
    onConfirm?.();
  };

  return (
    <Modal open={open} onClose={onClose} className="w-[85vw] md:w-[458px] lg:w-[458px]">
      <div className="relative flex flex-col gap-4">
        {isLoading && <Loading />}
        {/* Title and Description */}
        <div className="flex flex-col gap-2 text-neutral-dark-gray">
          <Typography variant="title">Confirm Downgrade</Typography>
          <Typography variant="paragraph">
            Your plan will switch to <span className="font-semibold">{planName} Plan</span> immediately, and the changes below will take
            effect:
          </Typography>
        </div>

        {/* Plan Header */}
        <div className="flex flex-row items-center gap-3">
          {iconSvg && (
            <div className="flex h-10 w-10 flex-shrink-0 [&>svg]:h-full [&>svg]:w-full" dangerouslySetInnerHTML={{ __html: iconSvg }} />
          )}
          <Typography variant="title">{planName}</Typography>
        </div>

        {/* Features List */}
        <div className="relative">
          <ul className="flex max-h-[200px] flex-col gap-1 overflow-y-auto rounded-lg border border-neutral-mid-gray bg-neutral-surface-gray p-4 pl-8 shadow-inner">
            {features.map((feature, index) => (
              <li key={index} className="list-disc text-neutral-dark-gray">
                <Typography variant="paragraph">{feature}</Typography>
              </li>
            ))}
          </ul>
          {/* Fade gradient at bottom to indicate scrollable content */}
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 rounded-b-lg bg-gradient-to-t from-neutral-surface-gray to-transparent" />
        </div>

        {/* Important Warning */}
        <Alert
          type="danger"
          className="border border-danger"
          message={
            <div className="flex flex-col gap-1">
              <Typography variant="action" className="text-danger">
                Important
              </Typography>
              <Typography variant="paragraph" className="text-danger">
                Once you downgrade, all existing data and settings from your current plan will be permanently deleted and cannot be
                recovered.
              </Typography>
            </div>
          }
          noIcon
        />

        {/* Action Buttons */}
        <div className="flex flex-row items-center justify-end gap-3">
          <Button type="button" size="lg" variant="secondary" onClick={handleCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="button" size="lg" variant="primary" onClick={handleConfirm} disabled={isLoading}>
            Confirm Downgrade
          </Button>
        </div>
      </div>
    </Modal>
  );
};
